/**
 * 视频优化工具函数
 * 使用 Cloudflare 的视频转换功能来优化视频加载性能
 */

export interface VideoOptimizationOptions {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'mp4' | 'webm' | 'auto';
  fps?: number;
}

export interface VideoThumbnailOptions {
  width?: number;
  height?: number;
  quality?: number;
  time?: string; // 提取帧的时间点，例如 "3s", "1m30s"
  format?: 'jpg' | 'png';
  fit?: 'contain' | 'cover' | 'scale-down';
}

// 🔥 新增：性能监控和缓存
const thumbnailCache = new Map<string, { url: string; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5分钟缓存
const PERFORMANCE_MARKERS = new Map<string, number>();

/**
 * 🔥 新增：性能监控函数
 */
function markPerformance(name: string): void {
  if (typeof performance !== 'undefined') {
    PERFORMANCE_MARKERS.set(name, performance.now());
  }
}

function measurePerformance(name: string): number {
  if (typeof performance !== 'undefined') {
    const startTime = PERFORMANCE_MARKERS.get(name);
    if (startTime) {
      const duration = performance.now() - startTime;
      console.log(`⏱️ ${name} took ${duration.toFixed(2)}ms`);
      PERFORMANCE_MARKERS.delete(name);
      return duration;
    }
  }
  return 0;
}

/**
 * 🔥 新增：缓存管理函数
 */
function getCachedThumbnail(key: string): string | null {
  const cached = thumbnailCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    console.log('🎬 Using cached thumbnail:', key);
    return cached.url;
  }
  if (cached) {
    thumbnailCache.delete(key);
  }
  return null;
}

function setCachedThumbnail(key: string, url: string): void {
  thumbnailCache.set(key, { url, timestamp: Date.now() });
  console.log('🎬 Cached thumbnail:', key);
}

/**
 * 🔥 新增：生成缓存键
 */
function generateCacheKey(
  originalUrl: string,
  width: number,
  height: number,
  quality: number,
  time: string,
  format: string,
  fit: string
): string {
  return `${originalUrl}_${width}x${height}_q${quality}_t${time}_${format}_${fit}`;
}

/**
 * 优化视频 URL
 * @param originalUrl 原始视频链接
 * @param options 优化选项
 * @returns 优化后的视频链接
 */
export function optimizeVideoUrl(
  originalUrl: string, 
  options: VideoOptimizationOptions = {}
): string {
  // 如果不是 cdn.wan27ai.co 域名的视频，直接返回原始 URL
  if (!originalUrl || !originalUrl.includes('cdn.wan27ai.co')) {
    return originalUrl;
  }

  const {
    width,
    height,
    quality = 75, // 降低默认质量以减小文件大小
    format = 'auto',
    fps
  } = options;

  // 构建 Cloudflare 视频转换参数
  const transformParams: string[] = [];

  if (width) {
    transformParams.push(`width=${width}`);
  }

  if (height) {
    transformParams.push(`height=${height}`);
  }

  if (quality) {
    transformParams.push(`quality=${quality}`);
  }

  if (format && format !== 'auto') {
    transformParams.push(`format=${format}`);
  }

  if (fps) {
    transformParams.push(`fps=${fps}`);
  }

  // 如果没有任何转换参数，返回原始 URL
  if (transformParams.length === 0) {
    return originalUrl;
  }

  // 根据Cloudflare文档，正确的URL格式应该是：
  // https://cdn.wan27ai.co/cdn-cgi/media/<OPTIONS>/<FULL_ORIGINAL_URL>
  const transformPath = `/cdn-cgi/media/${transformParams.join(',')}`;
  
  // 确保原始URL是完整的URL
  const fullOriginalUrl = originalUrl.startsWith('http') ? originalUrl : `https://${originalUrl}`;
  
  return `https://cdn.wan27ai.co${transformPath}/${fullOriginalUrl}`;
}

/**
 * 提取原始视频URL（去除优化参数）
 * @param url 可能包含优化参数的URL
 * @returns 原始视频URL
 */
function extractOriginalVideoUrl(url: string): string {
  // 如果URL包含 /cdn-cgi/media/ 说明是优化过的URL，需要提取原始URL
  if (url.includes('/cdn-cgi/media/')) {
    // 从优化URL中提取原始URL
    // 格式: https://cdn.wan27ai.co/cdn-cgi/media/[参数]/[原始URL]
    const parts = url.split('/cdn-cgi/media/');
    if (parts.length >= 2) {
      // 找到最后一个以 https:// 开始的部分
      const afterMedia = parts[1];
      const httpIndex = afterMedia.indexOf('https://');
      if (httpIndex !== -1) {
        const originalUrl = afterMedia.substring(httpIndex);
        // 🔥 关键修复：解码URL，确保返回未编码的原始URL
        return decodeURIComponent(originalUrl);
      }
    }
  }
  
  // 🔥 修复：如果输入URL本身被编码了，也要解码
  return decodeURIComponent(url);
}

/**
 * 🔥 优化的视频封面图片生成函数
 * 添加缓存、性能监控和错误处理
 * @param originalUrl 原始视频URL
 * @param width 图片宽度，默认386
 * @param height 图片高度，默认290
 * @param quality 图片质量，默认98
 * @param time 提取帧的时间点，默认"3s"
 * @param format 图片格式，默认"jpg"
 * @param fit 图片适配方式，默认"cover"
 * @returns 预览图URL
 */
export function generateVideoThumbnailUrl(
  originalUrl: string,
  width: number = 386,
  height: number = 290,
  quality: number = 98,
  time: string = "3s",
  format: 'jpg' | 'png' = 'jpg',
  fit: 'contain' | 'cover' | 'scale-down' = 'cover'
): string {
  markPerformance('generateVideoThumbnailUrl');

  // 如果不是 cdn.wan27ai.co 域名的视频，直接返回空字符串
  if (!originalUrl || !originalUrl.includes('cdn.wan27ai.co')) {
    measurePerformance('generateVideoThumbnailUrl');
    return '';
  }

  // 🔥 新增：检查缓存
  const cacheKey = generateCacheKey(originalUrl, width, height, quality, time, format, fit);
  const cachedUrl = getCachedThumbnail(cacheKey);
  if (cachedUrl) {
    measurePerformance('generateVideoThumbnailUrl');
    return cachedUrl;
  }

  // 🔥 关键修复：提取原始视频URL，避免从优化过的视频生成封面
  const cleanOriginalUrl = extractOriginalVideoUrl(originalUrl);

  // 🔥 修复：确保时间格式正确，避免0.1s这样的短时间
  let safeTime = time;
  if (time.includes('s') && parseFloat(time) < 1) {
    safeTime = '1s'; // 最小1秒
  }

  // 构建封面图片转换参数
  const transformParams: string[] = [
    'mode=frame', // 指定为帧提取模式
    `time=${safeTime}`,
    `width=${width}`,
    `height=${height}`,
    `quality=${quality}`,
    `format=${format}`,
    `fit=${fit}`
  ];

  // 根据您提供的成功示例，正确的URL格式应该是：
  // https://cdn.wan27ai.co/cdn-cgi/media/mode=frame,time=3s,width=386,height=290,quality=98,format=jpg,fit=cover/https://cdn.wan27ai.co/wan2.2/1.mp4
  const transformPath = `/cdn-cgi/media/${transformParams.join(',')}`;
  
  // 🔥 修复：确保原始URL是完整的URL，不进行URL编码
  const fullOriginalUrl = cleanOriginalUrl.startsWith('http') ? cleanOriginalUrl : `https://${cleanOriginalUrl}`;
  
  // 🔥 关键修复：不进行URL编码，直接拼接，与成功示例保持一致
  const thumbnailUrl = `https://cdn.wan27ai.co${transformPath}/${fullOriginalUrl}`;
  
  // 🔥 新增：缓存结果
  setCachedThumbnail(cacheKey, thumbnailUrl);
  
  measurePerformance('generateVideoThumbnailUrl');
  return thumbnailUrl;
}

/**
 * 🔥 新增：高性能的预览图预加载函数
 */
export function preloadThumbnailOptimized(
  thumbnailUrl: string,
  priority: 'high' | 'low' = 'low'
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!thumbnailUrl) {
      resolve();
      return;
    }

    const timeout = priority === 'high' ? 5000 : 10000; // 高优先级5秒，低优先级10秒
    const startTime = performance.now();

    const img = new Image();
    
    const cleanup = () => {
      img.onload = null;
      img.onerror = null;
    };

    img.onload = () => {
      cleanup();
      const duration = performance.now() - startTime;
      console.log(`🎬 Thumbnail preload successful (${duration.toFixed(2)}ms):`, thumbnailUrl);
      resolve();
    };
    
    img.onerror = (error) => {
      cleanup();
      const duration = performance.now() - startTime;
      console.error(`🎬 Thumbnail preload failed (${duration.toFixed(2)}ms):`, thumbnailUrl, error);
      reject(new Error(`Thumbnail preload failed: ${thumbnailUrl}`));
    };
    
    // 设置超时处理
    const timeoutId = setTimeout(() => {
      cleanup();
      const duration = performance.now() - startTime;
      console.error(`🎬 Thumbnail preload timeout (${duration.toFixed(2)}ms):`, thumbnailUrl);
      reject(new Error(`Thumbnail preload timeout: ${thumbnailUrl}`));
    }, timeout);
    
    // 修改onload和onerror以清除超时
    const originalOnLoad = img.onload;
    const originalOnError = img.onerror;
    
    img.onload = () => {
      clearTimeout(timeoutId);
      if (originalOnLoad) originalOnLoad.call(img);
    };
    
    img.onerror = (error) => {
      clearTimeout(timeoutId);
      if (originalOnError) originalOnError.call(img, error);
    };
    
    img.src = thumbnailUrl;
  });
}

/**
 * 🔥 新增：批量预加载预览图
 */
export function batchPreloadThumbnails(
  urls: string[],
  priority: 'high' | 'low' = 'low',
  concurrency: number = 3
): Promise<void[]> {
  const chunks: string[][] = [];
  
  // 将URLs分块，控制并发数
  for (let i = 0; i < urls.length; i += concurrency) {
    chunks.push(urls.slice(i, i + concurrency));
  }
  
  // 串行处理每个块，块内并行
  return chunks.reduce(async (previousPromise, chunk) => {
    await previousPromise;
    const chunkPromises = chunk.map(url => 
      preloadThumbnailOptimized(url, priority).catch(error => {
        console.warn('Batch preload failed for:', url, error);
      })
    );
    return Promise.all(chunkPromises);
  }, Promise.resolve([]) as Promise<void[]>);
}

/**
 * 🔥 改进的不同使用场景预设的视频优化配置
 */
export const VIDEO_OPTIMIZATION_PRESETS = {
  // 缩略图/预览：很小尺寸，中等质量，快速加载
  thumbnail: {
    width: 320 as number, // 降低尺寸以提升加载速度
    quality: 60 as number, // 降低质量以减小文件大小
    fps: 15 as number // 降低帧率以减小文件大小
  },
  
  // 卡片预览：小尺寸，高质量显示
  card: {
    width: 480 as number, // 降低尺寸以提升加载速度
    quality: 75 as number, // 降低质量以减小文件大小
    fps: 20 as number // 降低帧率以减小文件大小
  },
  
  // 移动端：适合移动设备的尺寸，高清显示
  mobile: {
    width: 400 as number, // 降低移动端尺寸
    quality: 70 as number, // 降低移动端质量
    fps: 20 as number // 降低帧率
  },
  
  // 桌面端：高质量，适合大屏幕
  desktop: {
    width: 720 as number, // 降低桌面端尺寸
    quality: 80 as number, // 降低桌面端质量
    fps: 25 as number // 降低帧率
  },
  
  // Hero视频：高质量，适合首屏展示
  hero: {
    width: 1280 as number, // 降低Hero尺寸
    quality: 85 as number, // 降低Hero质量
    fps: 30 as number // 降低帧率
  }
};

/**
 * 🔥 新增：优化的预览图预设配置
 */
export const VIDEO_THUMBNAIL_PRESETS = {
  // 缩略图：小尺寸，快速加载
  thumbnail: {
    width: 240 as number,
    height: 180 as number,
    quality: 85 as number,
    time: '1s' as string,
    format: 'jpg' as const,
    fit: 'cover' as const
  },
  
  // 卡片预览：中等尺寸，平衡质量和速度
  card: {
    width: 320 as number,
    height: 240 as number,
    quality: 90 as number,
    time: '1s' as string,
    format: 'jpg' as const,
    fit: 'cover' as const
  },
  
  // Hero预览：大尺寸，高质量
  hero: {
    width: 480 as number,
    height: 360 as number,
    quality: 95 as number,
    time: '1s' as string,
    format: 'jpg' as const,
    fit: 'cover' as const
  },
  
  // 移动端预览：小尺寸，快速加载
  mobile: {
    width: 240 as number,
    height: 180 as number,
    quality: 85 as number,
    time: '1s' as string,
    format: 'jpg' as const,
    fit: 'cover' as const
  }
};

/**
 * 根据设备类型和使用场景自动选择最佳视频优化
 * @param originalUrl 原始视频链接
 * @param scenario 使用场景
 * @param deviceType 设备类型
 * @returns 优化后的视频链接
 */
export function getOptimizedVideoUrl(
  originalUrl: string,
  scenario: keyof typeof VIDEO_OPTIMIZATION_PRESETS = 'card',
  deviceType: 'mobile' | 'desktop' | 'auto' = 'auto'
): string {
  // 检测设备类型
  let finalDeviceType = deviceType;
  if (deviceType === 'auto') {
    // 在服务器端默认使用桌面端，避免水合错误
    if (typeof window === 'undefined') {
      finalDeviceType = 'desktop';
    } else {
      finalDeviceType = window.innerWidth < 768 ? 'mobile' : 'desktop';
    }
  }

  // 根据设备类型调整预设
  let preset = VIDEO_OPTIMIZATION_PRESETS[scenario];
  
  if (finalDeviceType === 'mobile') {
    // 移动端优化：使用专门的移动端预设，保持高质量
    preset = {
      ...preset,
      width: Math.min(preset.width || 720, VIDEO_OPTIMIZATION_PRESETS.mobile.width),
      quality: Math.max(preset.quality || 80, VIDEO_OPTIMIZATION_PRESETS.mobile.quality), // 确保不低于移动端质量
      fps: Math.max(preset.fps || 24, VIDEO_OPTIMIZATION_PRESETS.mobile.fps) // 确保流畅度
    };
  }

  return optimizeVideoUrl(originalUrl, preset);
}

/**
 * 根据使用场景自动生成视频封面图片
 * @param originalUrl 原始视频链接
 * @param scenario 使用场景
 * @param deviceType 设备类型
 * @param forceHighQuality 强制高质量模式
 * @returns 封面图片链接
 */
export function getOptimizedThumbnailUrl(
  originalUrl: string,
  scenario: keyof typeof VIDEO_THUMBNAIL_PRESETS = 'card',
  deviceType: 'mobile' | 'desktop' | 'auto' = 'auto',
  forceHighQuality: boolean = true
): string {
  // 检测设备类型
  let finalDeviceType = deviceType;
  if (deviceType === 'auto') {
    // 在服务器端默认使用桌面端，避免水合错误
    if (typeof window === 'undefined') {
      finalDeviceType = 'desktop';
    } else {
      finalDeviceType = window.innerWidth < 768 ? 'mobile' : 'desktop';
    }
  }

  // 根据设备类型选择最佳预设
  let finalScenario = scenario;
  if (finalDeviceType === 'mobile' && scenario === 'card') {
    // 移动端使用专门的移动端预设
    finalScenario = 'mobile';
  }

  let preset = VIDEO_THUMBNAIL_PRESETS[finalScenario];
  
  // 如果强制高质量模式，进一步优化
  if (forceHighQuality) {
    preset = {
      ...preset,
      quality: Math.min(preset.quality + 2, 98), // 增加2个质量点，最高98
    };
  }

  return generateVideoThumbnailUrl(
    originalUrl, 
    preset.width, 
    preset.height, 
    preset.quality, 
    preset.time, 
    preset.format, 
    preset.fit
  );
}

/**
 * 批量优化视频 URL
 * @param urls 视频 URL 数组
 * @param options 优化选项
 * @returns 优化后的视频 URL 数组
 */
export function batchOptimizeVideoUrls(
  urls: string[],
  options: VideoOptimizationOptions = {}
): string[] {
  return urls.map(url => optimizeVideoUrl(url, options));
}

/**
 * 批量生成视频封面图片 URL
 * @param urls 视频 URL 数组
 * @param options 封面图片选项
 * @returns 封面图片 URL 数组
 */
export function batchGenerateThumbnailUrls(
  urls: string[],
  width: number = 386,
  height: number = 290,
  quality: number = 98,
  time: string = "3s",
  format: 'jpg' | 'png' = 'jpg',
  fit: 'contain' | 'cover' | 'scale-down' = 'cover'
): string[] {
  return urls.map(url => generateVideoThumbnailUrl(url, width, height, quality, time, format, fit));
}

/**
 * 为视频添加预加载提示
 * @param videoElement 视频元素
 * @param optimizedUrl 优化后的视频 URL
 */
export function addVideoPreloadHints(
  videoElement: HTMLVideoElement,
  optimizedUrl: string
): void {
  // 添加预加载提示
  videoElement.preload = 'metadata';
  
  // 如果支持，添加资源提示
  if (typeof document !== 'undefined') {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = optimizedUrl;
    link.as = 'video';
    document.head.appendChild(link);
  }
}

/**
 * 为视频封面图片添加预加载提示
 * @param thumbnailUrl 封面图片 URL
 */
export function addThumbnailPreloadHints(thumbnailUrl: string): void {
  if (typeof document !== 'undefined' && thumbnailUrl) {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = thumbnailUrl;
    link.as = 'image';
    document.head.appendChild(link);
  }
}

 

/**
 * 🔥 已废弃：使用 generateVideoThumbnailUrl 替代
 * @deprecated 请使用 generateVideoThumbnailUrl 函数
 */
export function extractVideoFirstFrame(
  videoUrl: string,
  options: {
    width?: number
    height?: number
    quality?: number
    format?: 'jpg' | 'png'
    fit?: 'contain' | 'cover' | 'scale-down'
  } = {}
): string {
  console.warn('extractVideoFirstFrame is deprecated, use generateVideoThumbnailUrl instead');
  return generateVideoThumbnailUrl(
    videoUrl,
    options.width || 386,
    options.height || 290,
    options.quality || 98,
    '3s',
    options.format || 'jpg',
    options.fit || 'cover'
  );
}

/**
 * 首屏首帧预设配置 - 优化尺寸以匹配实际显示需求
 */
export const FIRST_FRAME_PRESETS = {
  // Hero首帧 - 优化尺寸匹配实际显示(386x290)，使用1.5倍密度
  hero: {
    width: 580,  // 386 * 1.5 ≈ 580
    height: 435, // 290 * 1.5 ≈ 435
    quality: 98, // 使用高质量，与成功示例一致
    format: 'jpg' as const,
    fit: 'cover' as const
  },
  
  // Hero移动端首帧 - 优化移动端性能
  heroMobile: {
    width: 386,  // 直接使用实际显示尺寸
    height: 290, // 直接使用实际显示尺寸
    quality: 98, // 使用高质量
    format: 'jpg' as const,
    fit: 'cover' as const
  },
  
  // Feature首帧 - 优化尺寸
  feature: {
    width: 480,  // 降低尺寸
    height: 360, // 降低尺寸
    quality: 98, // 使用高质量
    format: 'jpg' as const,
    fit: 'cover' as const
  },
  
  // Feature移动端首帧 - 优化移动端性能
  featureMobile: {
    width: 320,  // 进一步降低移动端尺寸
    height: 240, // 进一步降低移动端尺寸
    quality: 95, // 高质量
    format: 'jpg' as const,
    fit: 'cover' as const
  },
  
  // 缩略图首帧 - 高质量快速加载
  thumbnail: {
    width: 320,  // 降低尺寸
    height: 240, // 降低尺寸
    quality: 95, // 高质量
    format: 'jpg' as const,
    fit: 'cover' as const
  },
  
  // 移动端缩略图首帧 - 高质量快速加载
  thumbnailMobile: {
    width: 240,  // 进一步降低尺寸
    height: 180, // 进一步降低尺寸
    quality: 90, // 高质量
    format: 'jpg' as const,
    fit: 'cover' as const
  }
}

/**
 * 🔥 已废弃：使用 generateVideoThumbnailUrl 替代
 * @deprecated 请使用 generateVideoThumbnailUrl 函数
 */
export function getFirstFrameUrl(
  videoUrl: string,
  preset: 'hero' | 'heroMobile' | 'feature' | 'featureMobile' | 'thumbnail' | 'thumbnailMobile' = 'hero',
  deviceType: 'mobile' | 'desktop' | 'auto' = 'auto'
): string {
  console.warn('getFirstFrameUrl is deprecated, use generateVideoThumbnailUrl instead');
  
  // 根据预设和设备类型选择参数
  let width = 386, height = 290;
  
  if (preset === 'hero' || preset === 'heroMobile') {
    width = deviceType === 'mobile' ? 386 : 580;
    height = deviceType === 'mobile' ? 290 : 435;
  } else if (preset === 'feature' || preset === 'featureMobile') {
    width = deviceType === 'mobile' ? 320 : 480;
    height = deviceType === 'mobile' ? 240 : 360;
  } else if (preset === 'thumbnail' || preset === 'thumbnailMobile') {
    width = deviceType === 'mobile' ? 240 : 320;
    height = deviceType === 'mobile' ? 180 : 240;
  }
  
  return generateVideoThumbnailUrl(videoUrl, width, height, 98, '3s', 'jpg', 'cover');
}

/**
 * 预加载首帧图片
 * @param firstFrameUrl 首帧图片URL
 * @returns Promise<void>
 */
export function preloadFirstFrame(firstFrameUrl: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!firstFrameUrl) {
      console.log('🎬 No first frame URL provided, skipping preload')
      resolve()
      return
    }

    console.log('🎬 Preloading first frame:', firstFrameUrl)

    const img = new Image()
    
    img.onload = () => {
      console.log('🎬 First frame preload successful:', {
        url: firstFrameUrl,
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight
      })
      resolve()
    }
    
    img.onerror = (error) => {
      console.error('🎬 First frame preload failed:', {
        url: firstFrameUrl,
        error: error
      })
      reject(new Error(`First frame preload failed: ${firstFrameUrl}`))
    }
    
    // 设置超时处理
    const timeout = setTimeout(() => {
      console.error('🎬 First frame preload timeout:', firstFrameUrl)
      reject(new Error(`First frame preload timeout: ${firstFrameUrl}`))
    }, 10000) // 10秒超时
    
    img.onload = () => {
      clearTimeout(timeout)
      console.log('🎬 First frame preload successful:', {
        url: firstFrameUrl,
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight
      })
      resolve()
    }
    
    img.onerror = (error) => {
      clearTimeout(timeout)
      console.error('🎬 First frame preload failed:', {
        url: firstFrameUrl,
        error: error
      })
      reject(new Error(`First frame preload failed: ${firstFrameUrl}`))
    }
    
    img.src = firstFrameUrl
  })
}

 